import { LocalDate, LocalTime } from 'js-joda';
import {
  max as _max,
  min as _min,
  every,
  filter,
  includes,
  keys,
  map,
  nth,
  some,
  toInteger,
  toNumber,
} from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import { Option } from 'types';
import { SearchMode } from 'components/search/utils';
import { computeDisplayedTimes, getOpenDates } from 'utils/offerings';
import { consumer } from '@tock/coretypes';

// If this value is being updated, then change AVAILABLE_TICKETS_TOKEN_LIMIT in IndexConverterUtils.java
export const DEFAULT_MAX_SEARCH_PARTY_SIZE_DISPLAY = 20;

const shownTimes = ['19:00', '19:30', '20:00', '12:00', '10:00'];

export type ExperienceCardAction = 'TIME_PILL' | 'BOOK_NOW';

/**
 * ADDITIONAL IS NO LONGER USED IN THE NEW CODE, IT'S A BACKWARDS COMPATIBILITY ENTRY
 */
export type FilterType = 'TIME' | 'SIZE' | 'DATE' | 'TYPE' | 'TEXT' | 'CITY' | 'TAG' | 'ADDITIONAL';

export enum SearchNavEntityTypes {
  CITY = 'CITY',
  CUISINE = 'CUISINE',
  BUSINESS = 'BUSINESS',
  HEADING = 'HEADING',
  TEXT = 'TEXT',
  QUERY = 'QUERY',
  SHOP_ITEM = 'SHOP_ITEM',
  WINE_STYLE = 'WINE_STYLE',
  WINE_VARIETAL = 'WINE_VARIETAL',
  WINE_REGION = 'WINE_REGION',
  SHOP_BRAND = 'SHOP_BRAND',
  SHOP_EMPTY_STATE = 'SHOP_EMPTY_STATE',
}

export enum SearchSizeType {
  PARTY_SIZE = 'partySize',
  PURCHASE_SIZE = 'purchaseSize',
}

export type SearchBarOption =
  | SearchBarOptionCity
  | SearchBarOptionCuisine
  | SearchBarOptionBusiness
  | SearchBarOptionHeading
  | SearchBarOptionText
  | SearchBarOptionQuery
  | SearchBarOptionShopItem
  | SearchBarOptionWineStyle
  | SearchBarOptionWineVarietal
  | SearchBarOptionWineRegion
  | SearchBarOptionShopBrand;

export const SEARCH_BAR_SECTION_DIVIDER = 'SEARCH_BAR_SECTION_DIVIDER';

export type SearchBarOptionOrSectionDivider = SearchBarOption | typeof SEARCH_BAR_SECTION_DIVIDER;

export interface SearchBarOptionCity {
  primaryText: string;
  secondaryText: string;
  href: string;
  category: SearchNavEntityTypes.CITY;
  metroArea: consumer.NavSuggestResponse.IMetroAreaSuggestion;
}
export interface SearchBarOptionCuisine {
  primaryText: string;
  href: string;
  category: SearchNavEntityTypes.CUISINE;
}
export interface SearchBarOptionBusiness {
  primaryText: string;
  secondaryText: string;
  href: string;
  category: SearchNavEntityTypes.BUSINESS;
  business: consumer.NavSuggestResponse.IBusinessSuggestion;
}
export interface SearchBarOptionHeading {
  primaryText: string;
  category: SearchNavEntityTypes.HEADING;
}
export interface SearchBarOptionText {
  primaryText: string;
  href: string;
  category: SearchNavEntityTypes.TEXT;
}
export interface SearchBarOptionQuery {
  primaryText: string;
  href: string;
  category: SearchNavEntityTypes.QUERY;
  subCategory?:
    | SearchNavEntityTypes.WINE_STYLE
    | SearchNavEntityTypes.WINE_VARIETAL
    | SearchNavEntityTypes.WINE_REGION;
}

// shop
export interface SearchBarOptionShopItem {
  primaryText: string;
  secondaryText: string;
  href: string;
  category: SearchNavEntityTypes.SHOP_ITEM;
  item: consumer.ShopNavSuggestResponse.IItemSuggestion;
}
export interface SearchBarOptionWineStyle {
  primaryText: string;
  href: string;
  category: SearchNavEntityTypes.WINE_STYLE;
  style: consumer.IMetaTagSuggestion;
}
export interface SearchBarOptionWineVarietal {
  primaryText: string;
  href: string;
  category: SearchNavEntityTypes.WINE_VARIETAL;
  varietal: consumer.IMetaTagSuggestion;
}
export interface SearchBarOptionWineRegion {
  primaryText: string;
  secondaryText: string;
  href: string;
  category: SearchNavEntityTypes.WINE_REGION;
  region: consumer.IRegionTagSuggestion;
}
export interface SearchBarOptionShopBrand {
  primaryText: string;
  secondaryText: string;
  href: string;
  category: SearchNavEntityTypes.SHOP_BRAND;
  brand: consumer.ShopNavSuggestResponse.IShopBrandSuggestion;
}

export function getKeyForSearchBarOption(option: SearchBarOption): string {
  switch (option.category) {
    case SearchNavEntityTypes.BUSINESS: {
      return `${option.primaryText}-${option.business.domainName}`;
    }
    case SearchNavEntityTypes.CITY: {
      return `${option.primaryText}-${option.metroArea.slug}`;
    }
    case SearchNavEntityTypes.SHOP_ITEM: {
      return `${option.primaryText}-${option.item.id}`;
    }
    case SearchNavEntityTypes.SHOP_BRAND: {
      return `${option.primaryText}-${option.brand.id}`;
    }
    case SearchNavEntityTypes.WINE_VARIETAL: {
      return `${option.primaryText}-${option.varietal.id}`;
    }
    case SearchNavEntityTypes.WINE_REGION: {
      return `${option.primaryText}-${option.region.id}`;
    }
    case SearchNavEntityTypes.WINE_STYLE: {
      return `${option.primaryText}-${option.style.id}`;
    }
    default:
      return option.primaryText;
  }
}

export function isSelectableOption(option: SearchBarOptionOrSectionDivider) {
  if (option === 'SEARCH_BAR_SECTION_DIVIDER') {
    return false;
  }
  return (
    option.category === SearchNavEntityTypes.BUSINESS ||
    option.category === SearchNavEntityTypes.CITY
  );
}

export function computeInitialSearchState(
  availableDates?: DateMap,
  offeredDates?: DateMap,
  business?: consumer.IBusinessConfig,
  offerings?: consumer.ConsumerOfferings,
  size?: number,
  date?: string,
  time?: string,
  searchText?: string,
  enableExpandedCalendar?: boolean,
  ticketTypeId?: number,
  searchSizeType?: SearchSizeType
) {
  if (business && offerings) {
    const now = LocalDate.now();
    const displayedTimes = computeDisplayedTimes(business, offerings, now.toString());

    let selectedSize = defaultPartySize(offerings, ticketTypeId);
    const minSizeInternal = minSize(offerings, ticketTypeId, searchSizeType);
    const maxSizeInternal = maxSize(offerings, ticketTypeId, searchSizeType);
    if (size && toInteger(size) >= minSizeInternal && toInteger(size) <= maxSizeInternal) {
      selectedSize = toInteger(size);
    }

    const firstAvailableDate = _min(keys(availableDates)) || _min(keys(offeredDates));
    const selectedDateString = firstAvailableDate && !date ? firstAvailableDate : date;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstOpenDate = _min(keys(getOpenDates(offerings)))!;
    const selectedDate = selectedDateString
      ? LocalDate.parse(selectedDateString)
      : LocalDate.parse(firstOpenDate);

    const medianTime = oc(nth(displayedTimes, displayedTimes.length / 4)).value(shownTimes[0]);
    let defaultTimeString: string | undefined;

    for (const shownTimeString of shownTimes) {
      for (const openTimeString of offerings.openTime) {
        if (shownTimeString === openTimeString) {
          defaultTimeString = shownTimeString;
          break;
        }
      }
    }

    let validTime = time;
    if (
      displayedTimes.length > 0 &&
      time &&
      time < displayedTimes[0].value &&
      time > displayedTimes[displayedTimes.length - 1].value &&
      defaultTimeString
    ) {
      validTime = defaultTimeString;
    }

    const selectedTime = LocalTime.parse(validTime || defaultTimeString || medianTime);

    return {
      maxSize: maxSize(offerings, ticketTypeId, searchSizeType),
      minSize: minSize(offerings, ticketTypeId, searchSizeType),
      searchText,
      selectedSize,
      selectedDate,
      selectedTime,
      showDateSelector: false,
      showExpandedCalendar: enableExpandedCalendar,
      showGuestSelector: false,
      showTimeSelector: false,
    };
  }

  const selectedDate = date ? LocalDate.parse(date) : LocalDate.now();
  const selectedTime = time ? LocalTime.parse(time) : LocalTime.parse('07:00');
  const selectedSize = size ? toNumber(size) : 2;

  return {
    maxSize: 20,
    minSize: 1,
    searchText,
    selectedDate,
    selectedSize,
    selectedTime,
    showDateSelector: false,
    showExpandedCalendar: enableExpandedCalendar,
    showGuestSelector: false,
    showTimeSelector: false,
  };
}

function getExperiences(offerings: consumer.ConsumerOfferings, ticketTypeId?: number) {
  return filter(offerings.experience, (t) => !ticketTypeId || t.id === ticketTypeId);
}

function maxSize(
  offerings: consumer.ConsumerOfferings,
  ticketTypeId?: number,
  sizeType: 'purchaseSize' | 'partySize' = 'partySize'
): number {
  return (
    _max(
      map(getExperiences(offerings, ticketTypeId), (ticketType) => {
        return _max(ticketType[sizeType]);
      })
    ) ?? Infinity
  );
}

function minSize(
  offerings: consumer.ConsumerOfferings,
  ticketTypeId?: number,
  sizeType: 'purchaseSize' | 'partySize' = 'partySize'
): number {
  return (
    _min(
      map(getExperiences(offerings, ticketTypeId), (ticketType) => {
        return _min(ticketType[sizeType]);
      })
    ) || 0
  );
}

function defaultPartySize(offerings: consumer.ConsumerOfferings, ticketTypeId?: number): number {
  const experiences = getExperiences(offerings, ticketTypeId);
  const isToGoOnly = every(
    experiences,
    (e) => e.type === 'ORDER_DELIVERY' || e.type === 'ORDER_PICKUP'
  );
  if (isToGoOnly) {
    return minSize(offerings, ticketTypeId, 'partySize') || 1;
  }

  const offersTwo = some(experiences, (e) => includes(e.partySize, 2));
  if (offersTwo) {
    return 2;
  }
  return minSize(offerings, ticketTypeId, 'partySize') || 2;
}

export function generateGuestOptions(min: number, max: number): Option[] {
  const guests = [{ label: guestString(min), value: min, key: guestString(min) }];
  let count = min + 1;
  while (count <= max) {
    guests.push({ label: guestString(count), value: count, key: guestString(count) });
    count = count + 1;
  }
  return guests;
}

type OptionToTypeMapKeysValues = {
  [key in consumer.OfferingSearchType]: consumer.Offering.Type[] | undefined;
};

const OptionToTypeMap: OptionToTypeMapKeysValues = {
  ALL_EXPERIENCES: undefined,
  DINE_IN_EXPERIENCES: [
    'PRIX_FIXE',
    'DEPOSIT',
    'RESERVATION',
    'DEPRECATED_SPECIAL_TABLE',
    /* SEATED_EVENT results appear in both dine-in and event searches */ 'SEATED_EVENT',
  ],
  ORDER_DELIVERY: ['ORDER_DELIVERY'],
  ORDER_PICKUP: ['ORDER_PICKUP'],
  ONLINE_ORDERS: ['ORDER_DELIVERY', 'ORDER_PICKUP'],
  EVENT: ['DEPRECATED_EVENT', 'SEATED_EVENT', 'GA_EVENT'],
};

type BUSINESS_TYPE_WINERY = 'winery';

export type OfferingSearchTypeOrBusinessType = consumer.OfferingSearchType | BUSINESS_TYPE_WINERY;

export function isBusinessType(
  type?: OfferingSearchTypeOrBusinessType | string
): type is BUSINESS_TYPE_WINERY {
  return type === 'winery';
}

export interface OfferingTypeOption {
  label: string;
  value: OfferingSearchTypeOrBusinessType;
  key: OfferingSearchTypeOrBusinessType;
}

const offeringTypeOptions: OfferingTypeOption[] = [
  {
    label: 'All experiences',
    value: 'ALL_EXPERIENCES',
    key: 'ALL_EXPERIENCES',
  },
  {
    label: 'Dine in',
    value: 'DINE_IN_EXPERIENCES',
    key: 'DINE_IN_EXPERIENCES',
  },
  {
    label: 'Delivery',
    value: 'ORDER_DELIVERY',
    key: 'ORDER_DELIVERY',
  },
  {
    label: 'Pickup',
    value: 'ORDER_PICKUP',
    key: 'ORDER_PICKUP',
  },
  {
    label: 'Events',
    value: 'EVENT',
    key: 'EVENT',
  },
  {
    label: 'Wineries',
    value: 'winery',
    key: 'winery',
  },
];

export function generateOfferingTypeOptions(stateValue: SearchMode): OfferingTypeOption[] {
  const effectiveOptions = offeringTypeOptions;

  if (stateValue.stateType === 'city') {
    const shouldRemoveToGo = stateValue.metroArea.toGoBusinessCount === 0;
    const filteredOptions = effectiveOptions.filter((option) => {
      if (shouldRemoveToGo) {
        return option.value !== 'ORDER_DELIVERY' && option.value !== 'ORDER_PICKUP';
      } else {
        return true;
      }
    });
    return filteredOptions;
  }
  return effectiveOptions;
}

export const getOfferingTypeString = (type: OfferingSearchTypeOrBusinessType) => {
  const matchingOption = offeringTypeOptions.find((option) => option.value === type);
  return matchingOption ? matchingOption.label : undefined;
};

export const getOfferingTypeQueryParam = (type?: consumer.OfferingSearchType) => {
  if (!type) {
    return undefined;
  }
  return OptionToTypeMap[type];
};

function guestString(amount: number): string {
  return `${amount} ${amount === 1 ? 'guest' : 'guests'}`;
}

export const recommendedQueries: string[] = [
  'Asian',
  'Breakfast',
  'Burgers',
  'Chicken',
  'Dessert',
  'Healthy',
  'Pizza',
  'Mexican',
  'Chinese',
  'Wineries',
  'Wine',
  'Sandwiches',
  'Salads',
  'BBQ',
  'Delivery',
  'Takeout',
  'American',
  'Italian',
  'Thai',
  'Sushi',
  'Indian',
  'Patio',
  'Cocktails To Go',
  'French',
];
